<template>
  <div
    v-if="show"
    class="d-flex justify-center"
  >
    <div
      id="contentList"
      class="article-content my-2 mt-8 workspace-outline"
    >
      <div class="workspace-block">
      <h2 class="ml-3 mb-2">{{ $t('article.contentList.title') }}</h2>

      <draggable
        :list="localFlatBlocks"
        :group="{ name: 'tocBlocks', pull: false, put: false }"
        :disabled="!expanded"
        :force-fallback="true"
        :fallback-on-body="true"
        :fallback-tolerance="3"
        :animation="180"
        :scroll="true"
        :scroll-sensitivity="60"
        :scroll-speed="10"
        ghost-class="toc-drag-ghost"
        fallback-class="toc-drag-clone"
        @start="onDragStart"
        @end="onDragEnd"
      >
        <div
          v-for="(item, index) of (expanded ? localFlatBlocks : localFlatBlocks.slice(0, PREVIEW_COUNT))"
          :key="item.uid"
          class="toc-item"
          :style="{ paddingLeft: (item.level - 2) * 14 + 'px' }"
        >
          <v-icon v-if="expanded" small class="toc-drag-handle mr-1" color="grey darken-1">drag_indicator</v-icon>
          <v-chip
            x-small
            class="toc-level-chip mr-2"
            :class="{ 'ml-4': !expanded }"
            :color="levelColor(item.level)"
            text-color="white"
          >H{{ item.level }}</v-chip>
          <a
            class="toc-heading-link"
            :class="{ 'toc-empty': !stripTagsAndEntities(item.h) }"
            @click.prevent="scrollTo('h' + item.level, item.indexInPart)"
          >{{ stripTagsAndEntities(item.h) || $t('article.contentList.emptyHeading') }}</a>
        </div>
      </draggable>

      <div class="d-flex flex-wrap align-center text-caption-primary mt-2 mx-1">
        <span>{{ $t('article.contentList.title') }}</span>
        <VerticalDivider />
        <span>{{ $t('article.contentList.hint') }}</span>
        <template v-if="expanded">
          <VerticalDivider />
          <span class="grey--text">{{ $t('article.contentList.dragHint') }}</span>
        </template>
        <v-spacer />
        <v-btn
          v-if="localFlatBlocks.length > PREVIEW_COUNT"
          text
          x-small
          color="primary"
          class="toc-toggle-btn"
          @click="expanded = !expanded"
        >
          <v-icon x-small left>{{ expanded ? 'expand_less' : 'expand_more' }}</v-icon>
          {{ expanded
            ? $t('article.contentList.showLess')
            : $t('article.contentList.showAll', { count: localFlatBlocks.length })
          }}
        </v-btn>
      </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
    import Vue from 'vue';
    import {ArticlePartContent} from "@/model/Article";
    import {stripTagsAndEntities} from "@/utils/string";
    import VerticalDivider from "@/components/VerticalDivider.vue";
    import {Scroll} from "@/config/Behaviour";
    import draggable from 'vuedraggable';

    interface FlatItem {
        uid: number;
        level: number;
        h: string;
        indexInPart: number;
    }

    export default Vue.extend({
        name: 'ContentList',
        components: {VerticalDivider, draggable},
        data: () => ({
            localFlatBlocks: [] as FlatItem[],
            isDragging: false,
            expanded: false,
            PREVIEW_COUNT: 5,
        }),
        computed: {
            flatBlocks(): FlatItem[] {
                const flat: FlatItem[] = [];
                for (let i = 2; i <= 6; i++) {
                    const arr: ArticlePartContent[] = (this.$store.getters.article as any)['h' + i] || [];
                    arr.forEach((block, idx) => {
                        flat.push({ uid: block.uid, level: i, h: block.h, indexInPart: idx });
                    });
                }
                return flat;
            },
            show(): boolean {
                return this.flatBlocks.some(item => item.h && item.h.length > 0);
            },
        },
        watch: {
            flatBlocks: {
                immediate: true,
                handler(this: any, newVal: FlatItem[]) {
                    if (!this.isDragging) {
                        this.localFlatBlocks = [...newVal];
                    }
                },
            },
        },
        methods: {
            stripTagsAndEntities,
            levelColor(level: number): string {
                const colors: Record<number, string> = {
                    2: '#0887dc',
                    3: '#2196F3',
                    4: '#42A5F5',
                    5: '#64B5F6',
                    6: '#90CAF9',
                };
                return colors[level] || '#0887dc';
            },
            scrollTo(selector: string, index: number): void {
                this.$vuetify.goTo('#' + selector + 'hw' + (index ? index : ''), {
                    duration: Scroll.duration,
                    offset: Scroll.offset,
                });
            },
            onDragStart(): void {
                this.isDragging = true;
            },
            onDragEnd(evt: any): void {
                this.isDragging = false;
                const { oldIndex, newIndex } = evt;
                if (oldIndex === newIndex || oldIndex == null) return;

                // Only change the level of the moved block.
                // All others keep their original levels.
                const movedUid = this.localFlatBlocks[newIndex].uid;
                const updatedFlat: FlatItem[] = this.localFlatBlocks.map((item, idx) => {
                    if (item.uid !== movedUid) return item;
                    // New level = level of predecessor, or H2 if moved to first position
                    const newLevel = newIndex === 0
                        ? 2
                        : this.localFlatBlocks[newIndex - 1].level;
                    return { ...item, level: newLevel };
                });

                this.$store.commit('setArticleFromFlatBlocks',
                    updatedFlat.map(b => ({ uid: b.uid, level: b.level }))
                );
                // localFlatBlocks will be resynced by the watcher after Vuex updates
            },
        },
    });
</script>

<style lang="sass" scoped>
#contentList .toc-item
  display: flex
  align-items: center
  padding-top: 5px
  padding-bottom: 5px
  padding-right: 12px
  border-radius: 6px
  cursor: grab
  transition: background 0.12s ease

  &:hover
    background: rgba(0, 0, 0, 0.04)

  &:active
    cursor: grabbing

.toc-drag-handle
  opacity: 0.4
  flex-shrink: 0
  transition: opacity 0.12s ease

.toc-item:hover .toc-drag-handle
  opacity: 0.8

.toc-level-chip
  font-size: 10px !important
  height: 18px !important
  flex-shrink: 0

.toc-heading-link
  cursor: pointer
  text-decoration: none
  color: inherit
  white-space: nowrap
  overflow: hidden
  text-overflow: ellipsis
  font-size: 0.875rem

  &:hover
    text-decoration: underline

.toc-empty
  color: rgba(0, 0, 0, 0.35)
  font-style: italic

.toc-drag-ghost
  opacity: 0.0

.toc-toggle-btn
  font-size: 11px !important
  letter-spacing: 0.02em
</style>

<style lang="sass">
.toc-drag-clone
  box-shadow: 0 8px 24px rgba(0,0,0,0.18) !important
  border-radius: 8px !important
  background: #f4f6fb !important
  opacity: 0.95
  pointer-events: none
  padding: 4px 8px
  font-size: 0.875rem
</style>
